import { Component, Vue, Watch } from "vue-property-decorator";
import {
  MIRA_ALL_CLIENTS_SCOPE,
  MIRA_BRANCH_NONE,
  MIRA_BRANCH_OTHER,
  MIRA_EXCLUDED_KEY,
  MiraAvailabilityState,
  MiraBranchDocument,
  MiraPublishedPeriod,
  MiraSection,
  MiraSectionDocument,
  MiraTrendSeries,
} from "@/data/models/Mira";
import {
  getBranchesOfClient,
  getClientId,
  getClientInfo,
} from "@/data/repository/ClientRepository";
import {
  isMiraGroupMaster,
  loadBranchSection,
  loadCompanyMemberSection,
  loadGroupViewSection,
  loadInsurerSection,
  loadMarketSection,
  loadMemberSection,
  loadMiraSections,
} from "@/data/repository/MiraRepository";
import { withMarketOfOwnKind } from "@/utils/MiraMarketKind";
import {
  effectiveMarketCountry,
  marketCountryOf,
  marketCountryOption,
  marketCountryOptionName,
  marketDocumentForCountry,
  withMarketCountryOptions,
} from "@/utils/MiraMarketCountries";
import { companyViewOf } from "@/utils/MiraCompanyView";
import { getEffectiveUser } from "@/utils/ViewAs";
import { miraMarketClientsAccess, miraMarketCountriesAccess, miraPercentilesAccess } from "@/permissions/UserPermissions";
import { MiraComparisonModule } from "@/store/modules/MiraComparisonModule";
import { MiraPeriodModule } from "@/store/modules/MiraPeriodModule";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import {
  COLOR_MIRA_MARKET_BAR,
  COLOR_MIRA_MARKET_BAR_DARK,
  COLOR_MIRA_REFERENCE_BAR,
  COLOR_MIRA_REFERENCE_BAR_DARK,
  COLOR_TABLE_HEADER,
  COLOR_TABLE_HEADER_DARK,
} from "@/utils/colors";
import { formatDateToDayMonthYear } from "@/utils/DateHelper";
import { formatFloat } from "@/utils/LocalisationHelper";
import {
  MIRA_COMPARISON_BRANCHES,
  MIRA_COMPARISON_GROUP,
  MIRA_COMPARISON_INSURERS,
  MIRA_COMPARISON_MARKET,
  MIRA_COMPARISON_MEMBERS,
  MIRA_COMPARISON_NONE,
  MiraComparisonMode,
  MIRA_COMPARISON_REFERENCE_ID,
  MIRA_MARKET_OWN_ID,
  MIRA_MARKET_PROJECTS_ID,
  MiraComparisonView,
  buildBranchComparison,
  buildComparison,
  buildMarketComparison,
  membersSuffix,
} from "@/utils/MiraComparisonView";
import { MIRA_GROUP_VIEW_ID, MIRA_GROUP_VIEW_OWN_ID, buildGroupViewComparison } from "@/utils/MiraGroupViewComparison";
import { MiraSectionUnlocks, availablePeriods, collectUnlocks, miraPeriodName, periodRange } from "@/utils/MiraDataQuality";
import { MiraTile, publishedMetric, tidyCaption } from "@/utils/MiraDataQualityView";
import { setMiraPageTitle } from "@/utils/MiraEdition";
import { effectivePeriod } from "@/utils/MiraOverview";
import { MiraDryingContext } from "@/utils/MiraDryingView";
import { MiraSizedEntity, MiraStackEntity, buildStableEntityColors, comparisonUsesLevels } from "@/utils/MiraStackedTrendView";
import { hasTrend, isTotalMetric } from "@/utils/MiraTrendView";
import { themed } from "@/utils/Theme";
import { formatNumber } from "@/utils/Utils";
import {
  MiraComparisonCandidate,
  MiraComparisonKindOption,
  MiraSavedComparison,
  MiraSavedComparisonKind,
} from "@/data/models/MiraSavedComparison";
import {
  activeSavedComparisonOf,
  downwardChoiceOf,
  downwardMenuOptionsOf,
  inPickedOrder,
  savedComparisonIdOf,
  savedComparisonKindsOf,
  savedComparisonOption,
  savedComparisonsFor,
} from "@/utils/MiraSavedComparisons";
import { loadMiraMemberRoster } from "@/utils/MiraMemberRoster";
import {
  baselineKey,
  baselineNameKey,
  baselineScale,
  countMetricIds,
  impliedBaseline,
  scaledBaselinePeriod,
} from "@/utils/MiraBaseline";

/**
 * The figure that sizes an entity for its stable comparison colour: its projects in the chosen period.
 * Read from the business-development document (the member's own, or the branch document), never the
 * section on screen, so the colour order is the same on every Mira screen.
 */
const MIRA_SIZE_METRIC_ID = "MIRA-PRJ-001";

/**
 * What every Mira section screen shares: loading the section documents, the period picker, the header
 * lines (the period, its comparison, the run date), and the context the view models translate and
 * format through. A screen extends it and names its section (`section`) and its app bar title
 * (`titleKey`); everything specific to its figures stays in the screen and its view model.
 *
 * A base class rather than a copy per screen, so the period rules (the document decides which periods
 * exist, the rolling year first) cannot differ between Datenqualität and Trocknung.
 */
@Component
export default class MiraScreenBase extends Vue {
  isLoading = true;
  sections: Partial<Record<MiraSection, MiraSectionDocument>> = {};

  /**
   * The figure whose timeline the Verlauf card shows, set by clicking a period tile. `null` means the
   * default (the first period tile that has a trend); see {@link activeTrendMetricId}. Initialised with
   * `null`, never `undefined`, so it is reactive (ui-conventions, Reactivity).
   */
  selectedTrendMetricId: string | null = null;

  /**
   * Whether the signed-in client is a group master, so the Vergleich selector and the group comparison
   * apply. Read once on mount (it does not change in-session); a data field, not a getter, so the
   * selector appears after the load rather than on the loading frame. Initialised `false`.
   */
  isGroupMaster = false;

  /** The member clients' display names, keyed by client id. Filled when the comparison is turned on. */
  memberNames: Record<string, string> = {};

  /** Each member's own section document for this screen, keyed by client id (`null` when it has none). */
  memberSectionDocs: Record<string, MiraSectionDocument | null> = {};

  /**
   * Each member's own business-development document, keyed by client id, for the size that orders the
   * stable comparison colours (its projects in the chosen period). Loaded alongside the section
   * documents, cached in the repository; `null` where a member has none. Initialised `{}`, not
   * `undefined`, so it is reactive.
   */
  memberBusinessDocs: Record<string, MiraSectionDocument | null> = {};

  /**
   * The signed-in client's branch business-development document, for the size that orders the branches'
   * stable comparison colours. Loaded once on mount off a non-master, cached; `null` where there is none.
   * Initialised `null`, not `undefined`, so it is reactive.
   */
  branchBusinessDocument: MiraBranchDocument | null = null;

  /** Whether the member documents are being loaded, for the comparison card's single spinner. */
  membersLoading = false;

  /** Guards the one-time member load per screen visit; the repository caches across screens. */
  membersLoaded = false;

  /**
   * The signed-in client's branch comparison document for this screen, or `null` where there is none (a
   * single-branch client, Datenqualität, or a group master). Loaded once on mount so the "Standorte"
   * option can appear; initialised `null`, never `undefined`, so it is reactive (ui-conventions).
   */
  branchDocument: MiraBranchDocument | null = null;
  /** The per-insurer document (#2610137, 2026-11-03), or a master's pooled one; `null` turns "Versicherer" off. */
  insurerDocument: MiraBranchDocument | null = null;
  /** The business-development per-insurer document, which sizes the insurers for their stable colours. */
  insurerBusinessDocument: MiraBranchDocument | null = null;

  /**
   * The Markt document of this screen's section (#2610072): every project of every published Mandant once.
   * Loaded on mount (one read, cached for the session) so the "Markt" option can appear; `null` where there
   * is none. Markt je Mandant (`miraMarket/clients_*`) is no longer read (Hans, #2610112 X6a).
   */
  marketAllDocument: MiraSectionDocument | null = null;

  /** The chosen country's Markt document (#2611022), loaded when an IRES user picks a country; else `null`. */
  marketCountryDocument: MiraSectionDocument | null = null;

  /**
   * The Markt every card, caption and timeline reads: the chosen country's where one is in effect, else every
   * country's. While the country's is loading there is none, never every country's under the country's name.
   */
  get marketProjectsDocument(): MiraSectionDocument | null {
    return marketDocumentForCountry(this.marketCountry, this.marketCountryDocument, this.marketAllDocument);
  }

  /** The countries the Markt can be narrowed to, for IRES users only (#2611022); none for anyone else. */
  get marketCountries(): string[] {
    const countries = this.marketAllDocument?.countries ?? [];
    // No access check without countries (a Markt document from before lists none).
    return countries.length > 0 && miraMarketCountriesAccess(getEffectiveUser()) ? countries : [];
  }

  /** The country of the Markt in effect: the chosen one where it is still offered, else `null` (every country). */
  get marketCountry(): string | null {
    return effectiveMarketCountry(MiraComparisonModule.marketCountry, this.marketCountries);
  }

  /** Loads the chosen country's Markt, of the client's own kind like every country's (#2611022). */
  @Watch("marketCountry")
  async onMarketCountry(country: string | null) {
    if (!country) {
      return;
    }
    const document = await loadMarketSection("projects", this.section, country);
    // A newer choice while this one loaded wins.
    if (this.marketCountry === country) {
      this.marketCountryDocument = withMarketOfOwnKind(document, this.sections[this.section] ?? null);
    }
  }

  /**
   * A group member's view of its group for this screen's section (`groupView_{section}`, #2610112 X6d),
   * loaded on mount off a master so the "Gruppe" option can appear; `null` where there is none.
   */
  groupViewDocument: MiraSectionDocument | null = null;

  /** The section this screen shows. Each screen overrides it. */
  get section(): MiraSection {
    return MiraSection.DATA_QUALITY;
  }

  /** The i18n key of the app bar title. Each screen overrides it. */
  get titleKey(): string {
    return "pages.mira";
  }

  get tableHeaderColor() {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  /**
   * What the view models need. Reads `$vuetify.theme.dark` here, so every getter built on it re-renders
   * when the theme changes.
   */
  get viewContext(): MiraDryingContext {
    return {
      t: (key, params) => this.$t(key, params ?? []).toString(),
      te: (key) => this.$te(key),
      formatNumber: (value) => formatNumber(value),
      formatDecimal: (value, digits) => formatFloat(value, digits),
      dark: this.$vuetify.theme.dark,
      locale: this.$i18n.locale,
    };
  }

  get document(): MiraSectionDocument | null {
    return this.sections[this.section] ?? null;
  }

  /**
   * The screen's KPI tiles. The base returns none; each screen overrides it (a subclass getter
   * replaces the base one in vue-class-component). Declared here so the base can split them into the
   * "Stand heute" block and the period tiles, and drive the Verlauf selection, without each screen
   * repeating that logic.
   */
  get tiles(): MiraTile[] {
    return [];
  }

  /**
   * The tiles scoped to the chosen period, rendered under the period header. On a group master each
   * tile's caption gains the "über X von Y Betrieben" suffix where the figure carries its member
   * counts; on a client document the suffix is absent, so this is a no-op there.
   *
   * Every Mira figure is now a figure of the period ("Mira is a look back", #2610040): the "Stand heute"
   * snapshot block was retired, so there is no snapshot tile to split off here anymore.
   */
  get periodTiles(): MiraTile[] {
    return this.tiles
      // Upwards alone, a figure the Markt / Gruppe never carries is hidden; with a comparison downwards on
      // too, every figure stays, since its parts can still be compared.
      .filter((tile) => !this.showMarketComparison || this.showDownwardComparison || this.inMarket(tile.id))
      .filter((tile) => !this.showGroupViewComparison || this.showDownwardComparison || this.inGroupView(tile.id))
      .map((tile) => this.withMembersSuffix({ ...tile, caption: tidyCaption(tile.caption) }))
      .map((tile) => this.withExceptionCaption(tile))
      .map((tile) => this.withMarketCaption(tile));
  }

  /**
   * Appends "n als Ausnahme ausgelassen" where IRES Cloud left projects out of the figure because their
   * event was recorded late (Mira-Ausnahmen, Hans R8, #2610040): the count travels as the figure's
   * `breakdown.excluded`. Shown to everyone who sees the tile (Taimoor, 2026-10-16).
   */
  withExceptionCaption(tile: MiraTile): MiraTile {
    const excluded = this.period?.metrics[tile.id]?.breakdown?.[MIRA_EXCLUDED_KEY] ?? 0;
    if (!(excluded > 0)) {
      return tile;
    }
    const note = this.$t("mira.exceptions.leftOut", [formatNumber(excluded)]).toString();
    return { ...tile, caption: tile.caption ? `${tile.caption} · ${note}` : note };
  }

  /** Appends the group's "über X von Y Betrieben" suffix to a tile's caption, where it applies. */
  withMembersSuffix(tile: MiraTile): MiraTile {
    const suffix = membersSuffix(this.period?.metrics[tile.id]?.members, this.viewContext);
    if (!suffix) {
      return tile;
    }
    return { ...tile, caption: tile.caption ? `${tile.caption} · ${suffix}` : suffix };
  }

  /**
   * In Markt mode only, appends a short "Markt X" caption (the market median) to a tile that carries a
   * market block, so the default view is unchanged. Gated on the stored comparison (the store), not on
   * {@link upwardMode} or {@link showMarketComparison}, to avoid a computed cycle with
   * {@link marketAvailable}, which decides the offered options. Without a Markt value no caption is added,
   * so a stored "Markt" this screen does not offer adds nothing.
   */
  withMarketCaption(tile: MiraTile): MiraTile {
    if (this.storedUpwardMode !== MIRA_COMPARISON_MARKET || this.isAllClients) {
      return tile;
    }
    // The Markt je Projekt value of the figure, beside the company's own (#2610072).
    const market = publishedMetric(this.marketProjectsDocument?.periods[this.selectedPeriod] ?? null, tile.id);
    if (!market || market.value === null || market.value === undefined) {
      return tile;
    }
    const value = this.trendFormatter(tile.id)(market.value);
    const clients = this.marketClientsText(tile.id);
    const marketText = clients
      ? this.$t("mira.market.tileCaptionWithClients", [value, clients]).toString()
      : this.$t("mira.market.tileCaption", [value]).toString();
    return { ...tile, caption: tile.caption ? `${tile.caption} · ${marketText}` : marketText };
  }

  /** The Markt row of the ranking card with its Mandanten beside its cases, for IRES users (#2611021). */
  private withMarketClients(view: MiraComparisonView, metricId: string): MiraComparisonView {
    const clients = this.marketClientsText(metricId);
    if (!clients) {
      return view;
    }
    const rows = view.rows.map((row) =>
      row.id === MIRA_MARKET_PROJECTS_ID ? { ...row, casesText: row.casesText ? `${row.casesText} · ${clients}` : clients } : row
    );
    return { ...view, rows };
  }

  /** Whether the Markt figures name their Mandanten (IRES only, #2611021). */
  get showMarketClients(): boolean {
    return miraMarketClientsAccess(getEffectiveUser());
  }

  /**
   * "17 Mandanten": how many Mandanten the Markt figure is built from in the chosen period, as IRES Cloud
   * counts them (`members.contributing`), for IRES users only (Hans, #2611021). `null` for anyone else, or
   * where the Markt document carries no count.
   */
  marketClientsText(metricId: string): string | null {
    // Both reactive reads first, then branch (ui-conventions, Reactivity).
    const show = this.showMarketClients;
    const members = this.marketProjectsDocument?.periods[this.selectedPeriod]?.metrics[metricId]?.members;
    if (!show || !members) {
      return null;
    }
    return this.$t("mira.market.clients", [this.viewContext.formatNumber(members.contributing)]).toString();
  }

  /** Whether the user sees the P15 / P85 percentiles. Reactive: a self role change takes effect in-session. */
  get showPercentiles(): boolean {
    return miraPercentilesAccess(getEffectiveUser());
  }

  /** The timelines the document carries, or `undefined` for an older document without them. */
  get trends(): Record<string, MiraTrendSeries> | undefined {
    return this.document?.trends;
  }

  /** Whether a figure has a usable timeline (so its tile is selectable and the Verlauf card can show it). */
  trendAvailable(metricId: string): boolean {
    return hasTrend(this.trends, metricId);
  }

  /**
   * A period tile is selectable when it has a trend (so the Verlauf card can show it). In comparison mode
   * **every** period tile is selectable, so a figure without a timeline can still be compared across the
   * group.
   */
  isTileSelectable(tile: MiraTile): boolean {
    // In Markt mode too, every tile stays selectable (Taimoor, 2026-10-27): a figure without a Markt opens
    // the card with the "keinen Marktwert" note rather than a tile that cannot be clicked.
    return this.comparisonActive ? true : this.trendAvailable(tile.id);
  }

  /**
   * Whether the Markt carries the figure at all in the chosen period, with a value or withheld for too few
   * companies. A figure it never carries (a count or total, the GZ-timed Prozess figures) has no Markt by
   * rule, so its tile is hidden in Markt mode (Taimoor, 2026-10-27); one withheld today stays, with its note.
   */
  inMarket(metricId: string): boolean {
    return this.marketProjectsDocument?.periods[this.selectedPeriod]?.metrics[metricId] !== undefined;
  }

  /**
   * Whether the member's group view carries the figure in the chosen period, with a value or withheld. A
   * figure a group never carries (one that cannot be pooled) is hidden in "Gruppe" mode, as in Markt mode.
   */
  inGroupView(metricId: string): boolean {
    return this.groupViewDocument?.periods[this.selectedPeriod]?.metrics[metricId] !== undefined;
  }

  /** Whether the Markt has a value for the figure in the chosen period (#2610072). */
  hasMarketValue(metricId: string): boolean {
    return publishedMetric(this.marketProjectsDocument?.periods[this.selectedPeriod] ?? null, metricId) !== null;
  }

  /**
   * The figure the Verlauf card shows: the clicked one where it still has a trend among the period
   * tiles, else the first period tile that has one, else none (the card is hidden). Reading the
   * period tiles first keeps the default following a period change.
   */
  get activeTrendMetricId(): string | null {
    const selectable = this.periodTiles.filter((tile) => this.trendAvailable(tile.id)).map((tile) => tile.id);
    const selected = this.selectedTrendMetricId;
    if (selected && selectable.includes(selected)) {
      return selected;
    }
    return selectable[0] ?? null;
  }

  /** The tile whose figure the Verlauf card shows, for its title. */
  get activeTrendTile(): MiraTile | null {
    const active = this.activeTrendMetricId;
    return active ? this.periodTiles.find((tile) => tile.id === active) ?? null : null;
  }

  /** "Verlauf: <tile title>", the Verlauf card's title. */
  get trendCardTitle(): string {
    return this.activeTrendTile ? this.$t("mira.trend.title", [this.activeTrendTile.title]).toString() : "";
  }

  // --- Comparison (Vergleich): upwards (Markt, Gruppe) and downwards (Mandanten, Standorte) ---

  /**
   * The comparisons chosen last, shared across the Mira screens through the store. They outlive a sign-out
   * and a move to another screen, so they may name an option this client or screen does not offer: read
   * {@link upwardMode} / {@link downwardMode} instead, except where that would be a computed cycle.
   */
  get storedUpwardMode(): MiraComparisonMode {
    return MiraComparisonModule.upward;
  }

  get storedDownwardMode(): MiraComparisonMode {
    return MiraComparisonModule.downward;
  }

  /**
   * The comparisons in effect: the chosen ones where this screen offers them, else "Kein Vergleich" (Taimoor,
   * #2610112: Klünder's "Standorte" reached SVT Master Client after a sign-in, which offers no branches, so
   * the selector showed as active with nothing ticked). Every reader of a comparison goes through these.
   */
  get upwardMode(): MiraComparisonMode {
    // Read the reactive values first, then branch (ui-conventions, Reactivity).
    const stored = this.storedUpwardMode;
    const options = this.upwardOptions;
    return options.includes(stored) ? stored : MIRA_COMPARISON_NONE;
  }

  get downwardMode(): MiraComparisonMode {
    const stored = this.storedDownwardMode;
    const options = this.downwardOptions;
    return options.includes(stored) ? stored : MIRA_COMPARISON_NONE;
  }

  /**
   * The comparisons upwards (Hans, #2610137, 2026-11-02): "Gruppe" for a member whose group view has a
   * value on this screen, and "Markt" whenever a figure on this screen carries a market block for the chosen
   * period. A master has no group above it.
   */
  get upwardOptions(): MiraComparisonMode[] {
    const options: MiraComparisonMode[] = [MIRA_COMPARISON_NONE];
    // "Alle Mandanten" offers no comparison for now (#2610111, D5).
    if (this.isAllClients) {
      return options;
    }
    if (this.groupViewAvailable) {
      options.push(MIRA_COMPARISON_GROUP);
    }
    if (this.marketAvailable) {
      options.push(MIRA_COMPARISON_MARKET);
    }
    return options;
  }

  /**
   * The comparison menu upwards: {@link upwardOptions}, with a country's Markt after "Markt" for each country
   * an IRES user may narrow it to (#2611022).
   */
  get upwardMenuOptions(): string[] {
    return withMarketCountryOptions(this.upwardOptions, MIRA_COMPARISON_MARKET, this.marketCountries);
  }

  /** The ticked entry of the menu upwards: a country's Markt where one is in effect. */
  get upwardMenuValue(): string {
    const mode = this.upwardMode;
    const country = this.marketCountry;
    return mode === MIRA_COMPARISON_MARKET && country ? marketCountryOption(country) : mode;
  }

  /**
   * The comparisons downwards: a group master gets "Mandanten der Gruppe" (its view is the group, never its
   * own branches); any other client gets "Standorte" where this screen has a branch document. Never both.
   */
  get downwardOptions(): MiraComparisonMode[] {
    const options: MiraComparisonMode[] = [MIRA_COMPARISON_NONE];
    if (this.isAllClients) {
      return options;
    }
    if (this.isGroupMaster) {
      options.push(MIRA_COMPARISON_MEMBERS);
    } else if (this.branchDocument) {
      options.push(MIRA_COMPARISON_BRANCHES);
    }
    // The insurers it works for (#2610137, 2026-11-03), where IRES Cloud wrote a per-insurer document with
    // the chosen period: it carries no single months (Taimoor, 2026-11-03), so a month offers none.
    if (this.insurerDocument?.periods[this.selectedPeriod]) {
      options.push(MIRA_COMPARISON_INSURERS);
    }
    return options;
  }

  // --- Saved comparisons downwards (Hans, #2610137 tasks 22 / 24; Taimoor, 2026-11-02) ---

  /** What this screen compares downwards, and so the kinds of the saved comparisons it lists. */
  get savedComparisonKinds(): MiraSavedComparisonKind[] {
    return savedComparisonKindsOf(this.downwardOptions);
  }

  /** The user's saved comparisons of those kinds in this client; under Sicht auf the chosen user's. */
  get savedComparisons(): MiraSavedComparison[] {
    return savedComparisonsFor(getEffectiveUser(), getClientId(), this.savedComparisonKinds);
  }

  /** The downwards picker: "Kein Vergleich", all of the kind (ranked), then the saved comparisons by name. */
  get downwardMenuOptions(): string[] {
    return downwardMenuOptionsOf(this.downwardOptions, this.savedComparisons);
  }

  /** The saved comparison in effect (`activeSavedComparisonOf`), or `null` for all of the kind. */
  get activeSavedComparison(): MiraSavedComparison | null {
    return activeSavedComparisonOf(MiraComparisonModule.downwardSavedId, this.downwardMode, this.savedComparisons);
  }

  /** The picker's ticked option: the saved comparison in effect, else the kind or "Kein Vergleich". */
  get downwardMenuValue(): string {
    const saved = this.activeSavedComparison;
    return saved ? savedComparisonOption(saved.id) : this.downwardMode;
  }

  /** The kinds a saved comparison can be made of here, each with what it can pick, for "Vergleiche verwalten". */
  get comparisonKinds(): MiraComparisonKindOption[] {
    return this.manageableKinds.map((kind) => ({ kind, candidates: this.candidatesOf(kind) }));
  }

  /**
   * The kinds a comparison can be managed for, whatever the period: the insurers too on a single month, which
   * their documents do not carry, so the downwards icon stays with "Vergleiche verwalten" (Taimoor, 2026-11-03).
   */
  get manageableKinds(): MiraSavedComparisonKind[] {
    // Read the reactive values first, then branch (ui-conventions, Reactivity).
    const branches = this.branchDocument;
    const insurers = this.insurerDocument;
    if (this.isAllClients) {
      return [];
    }
    const kinds: MiraSavedComparisonKind[] = [];
    if (this.isGroupMaster) {
      kinds.push(MIRA_COMPARISON_MEMBERS as MiraSavedComparisonKind);
    } else if (branches) {
      kinds.push(MIRA_COMPARISON_BRANCHES as MiraSavedComparisonKind);
    }
    if (insurers) {
      kinds.push(MIRA_COMPARISON_INSURERS as MiraSavedComparisonKind);
    }
    return kinds;
  }

  /**
   * What a comparison of a kind can pick: the client's branches in its order, the members by name, the insurers
   * IRES Cloud has a row for by name.
   */
  candidatesOf(kind: MiraSavedComparisonKind): MiraComparisonCandidate[] {
    if (kind === MIRA_COMPARISON_BRANCHES) {
      return getBranchesOfClient().map((branch) => ({ id: branch.id, label: branch.name }));
    }
    const names = kind === MIRA_COMPARISON_MEMBERS ? this.memberNames : this.insurerNames;
    return Object.keys(names)
      .filter((id) => id !== MIRA_BRANCH_OTHER)
      .map((id) => ({ id, label: names[id] }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  /**
   * The insurers of the per-insurer document, key to short name (an unnamed key by itself), plus "Weitere
   * Versicherer" for the ones beyond the cap.
   */
  get insurerNames(): Record<string, string> {
    const document = this.insurerDocument;
    const names: Record<string, string> = {};
    Object.values(document?.periods ?? {}).forEach((period) => {
      Object.keys(period.branches ?? {}).forEach((id) => {
        names[id] = id === MIRA_BRANCH_OTHER ? this.$t("mira.comparison.insurerOther").toString() : document?.labels?.[id] ?? id;
      });
    });
    return names;
  }

  /** Before "Vergleiche verwalten" opens: a group's members are named only once they are loaded. */
  async prepareComparisons(): Promise<void> {
    if (this.isGroupMaster) {
      await this.ensureMembersLoaded();
    }
  }

  /** The insurers' reason line for a figure they do not carry, in place of the branches' wording. */
  private withInsurerReason(view: MiraComparisonView | null): MiraComparisonView | null {
    return view?.noComparisonForMetric ? { ...view, noComparisonKey: "mira.comparison.noMetricForInsurers" } : view;
  }

  /** The downwards ranking narrowed to a saved comparison's entities, in their picked order. */
  private narrowToSavedComparison(view: MiraComparisonView | null): MiraComparisonView | null {
    const saved = this.activeSavedComparison;
    if (!view || !saved) {
      return view;
    }
    const pickedNames = this.candidatesOf(saved.kind)
      .filter((candidate) => saved.entityIds.includes(candidate.id))
      .map((candidate) => candidate.label);
    return {
      ...view,
      rows: inPickedOrder(view.rows, saved.entityIds),
      withoutValue: view.withoutValue.filter((name) => pickedNames.includes(name)),
    };
  }

  /**
   * Whether any period tile of this screen carries a market block for the chosen period, so the "Markt"
   * option can appear. Read off the raw {@link tiles} (not {@link periodTiles}) so the Markt caption those
   * add cannot feed back into this getter.
   */
  get marketAvailable(): boolean {
    // Read the reactive values first, then branch (ui-conventions, Reactivity).
    const tiles = this.tiles;
    const market = this.marketAllDocument?.periods[this.selectedPeriod] ?? null;
    if (!this.period || this.isAllClients) {
      return false;
    }
    // By every country's Markt, so "Markt" stays offered while a country with fewer figures is chosen.
    return tiles.some((tile) => publishedMetric(market, tile.id) !== null);
  }

  /**
   * Whether "Gruppe" can be offered (#2610112 X6d): a member (never a master, whose comparison is its
   * members) whose group view has a value for any period tile of this screen in the chosen period.
   */
  get groupViewAvailable(): boolean {
    // Read the reactive values first, then branch (ui-conventions, Reactivity).
    const tiles = this.tiles;
    const groupView = this.groupViewDocument;
    if (!groupView || !this.period || this.isGroupMaster || this.isAllClients) {
      return false;
    }
    return tiles.some((tile) => publishedMetric(groupView.periods[this.selectedPeriod] ?? null, tile.id) !== null);
  }

  /** The group comparison of a member is on: "Gruppe" chosen upwards and offered. */
  get showGroupViewComparison(): boolean {
    return this.upwardMode === MIRA_COMPARISON_GROUP && this.groupViewAvailable;
  }

  /** The real branches of the signed-in client, name by id (deleted branches already dropped). */
  get branchNames(): Record<string, string> {
    const names: Record<string, string> = {};
    getBranchesOfClient().forEach((branch) => {
      names[branch.id] = branch.name;
    });
    return names;
  }

  /**
   * The branch reference row's label: the signed-in client's own name (its total across all branches),
   * falling back to "Gesamt" where no name is cached. Chosen over a bare "Gesamt" so it reads like the
   * group view's "Gruppe gesamt".
   */
  get branchReferenceLabel(): string {
    const info = getClientInfo();
    const name = info?.shortName || info?.companyName || info?.clientName;
    return name || this.$t("mira.comparison.referenceClient").toString();
  }

  /** The name of a comparison option, for the selector and the header: a saved comparison's own name. */
  comparisonName(option: string): string {
    const countryName = marketCountryOptionName(option, (key, params) => this.$t(key, params).toString(), this.$i18n?.locale ?? "de");
    if (countryName) {
      return countryName;
    }
    const savedId = savedComparisonIdOf(option);
    if (savedId) {
      return this.savedComparisons.find((comparison) => comparison.id === savedId)?.name ?? "";
    }
    return this.$t(`mira.comparison.modes.${option}`).toString();
  }

  /**
   * "Vergleich: Markt, Standorte im Mandanten" on the sticky header's first line, naming the comparisons
   * that are on (upwards first), or empty when none is.
   */
  get comparisonChoiceLabel(): string {
    // An IRES account's combined view says so where a comparison would be named (#2610111).
    if (this.isAllClients) {
      return this.$t("mira.allClients.label").toString();
    }
    const names = [this.upwardMenuValue, this.downwardMenuValue]
      .filter((option) => option !== MIRA_COMPARISON_NONE)
      .map((option) => this.comparisonName(option));
    return names.length === 0 ? "" : this.$t("mira.comparison.choice", [names.join(", ")]).toString();
  }

  /** The membership caveat under the header, where the chosen period starts before the group was recorded. */
  get membershipNote(): string {
    const seededAt = this.document?.membershipSeededAt;
    if (!this.period?.membershipAssumed || !seededAt) {
      return "";
    }
    return this.$t("mira.comparison.membershipAssumed", [formatDateToDayMonthYear(new Date(seededAt))]).toString();
  }

  /** The member comparison is on: a group master with "Mandanten der Gruppe" chosen. */
  get showMemberComparison(): boolean {
    return this.isGroupMaster && !this.isAllClients && this.downwardMode === MIRA_COMPARISON_MEMBERS;
  }

  /**
   * The branch comparison is on: a non-master with "Standorte" chosen and a branch document present. The
   * document guard matters because the chosen mode is shared across screens: a user who picks "Standorte"
   * on Geräte and moves to Datenqualität (no branch document) must fall back to the Verlauf card there.
   */
  get showBranchComparison(): boolean {
    return !this.isGroupMaster && !this.isAllClients && !!this.branchDocument && this.downwardMode === MIRA_COMPARISON_BRANCHES;
  }

  /** A comparison upwards is on: the Markt, or a member's Gruppe. */
  get showUpwardComparison(): boolean {
    return this.showMarketComparison || this.showGroupViewComparison;
  }

  /** The insurer comparison is on: "Versicherer" chosen and a per-insurer document present. */
  get showInsurerComparison(): boolean {
    return !this.isAllClients && !!this.insurerDocument && this.downwardMode === MIRA_COMPARISON_INSURERS;
  }

  /** A comparison downwards is on: the Mandanten of a group, the Standorte, or the insurers. */
  get showDownwardComparison(): boolean {
    return this.showMemberComparison || this.showBranchComparison || this.showInsurerComparison;
  }

  /** Whether the comparison cards replace the Verlauf card (any comparison, upwards or downwards, is on). */
  get showComparison(): boolean {
    return this.showUpwardComparison || this.showDownwardComparison;
  }

  /**
   * The Markt comparison is on: "Markt" chosen and a figure on the screen has a Markt value. It draws the
   * comparison cards like the group view (#2610072): the company against "Markt je Projekt" and "Markt je
   * Mandant".
   */
  get showMarketComparison(): boolean {
    return this.upwardMode === MIRA_COMPARISON_MARKET && this.marketAvailable;
  }

  /**
   * Whether any comparison (member, branch or market) is on, so every period tile is selectable (a figure
   * needs no timeline to be comparable) and the chosen tile, not the Verlauf one, drives the card.
   */
  get comparisonActive(): boolean {
    return this.showComparison;
  }

  /** The Markt row's label ("Markt"; "Markt je Projekt" until #2610137), "Markt Deutschland" for a country (#2611022). */
  get marketLabel(): string {
    const country = this.marketCountry;
    return country
      ? this.comparisonName(marketCountryOption(country))
      : this.$t("mira.market.entities.projects").toString();
  }

  /**
   * The figure the comparison card shows: the clicked period tile where it still exists, else the first
   * period tile. Unlike {@link activeTrendMetricId}, a figure needs no timeline to be comparable, so any
   * period tile qualifies.
   */
  get activeComparisonMetricId(): string | null {
    const ids = this.periodTiles.map((tile) => tile.id);
    const selected = this.selectedTrendMetricId;
    if (selected && ids.includes(selected)) {
      return selected;
    }
    return ids[0] ?? null;
  }

  /** The tile highlighted as selected: the comparison figure in any comparison mode, else the Verlauf figure. */
  get selectedTileId(): string | null {
    return this.comparisonActive ? this.activeComparisonMetricId : this.activeTrendMetricId;
  }

  /** "<figure title> nach Mandant" (members) or "… nach Standort" (branches), the comparison card's title. */
  get comparisonCardTitle(): string {
    const active = this.activeComparisonMetricId;
    const tile = active ? this.periodTiles.find((candidate) => candidate.id === active) ?? null : null;
    if (!tile) {
      return "";
    }
    // With both on, the card is about the parts, so the downwards title wins.
    const key = this.showBranchComparison
      ? "mira.comparison.cardTitleBranches"
      : this.showInsurerComparison
      ? "mira.comparison.cardTitleInsurers"
      : this.showMemberComparison
      ? "mira.comparison.cardTitle"
      : this.showMarketComparison
      ? "mira.market.cardTitle"
      : this.showGroupViewComparison
      ? "mira.comparison.cardTitleGroupView"
      : "mira.comparison.cardTitle";
    return this.$t(key, [tile.title]).toString();
  }

  /** The empty-state wording, in branch terms where the branch comparison is on, else the member default. */
  get comparisonEmptyText(): string | null {
    if (this.showDownwardComparison) {
      return this.showBranchComparison
        ? this.$t("mira.comparison.emptyBranches").toString()
        : this.showInsurerComparison
        ? this.$t("mira.comparison.emptyInsurers").toString()
        : null;
    }
    if (this.showMarketComparison) {
      return this.$t("mira.market.empty").toString();
    }
    if (this.showGroupViewComparison) {
      return this.$t("mira.comparison.groupView.empty").toString();
    }
    return this.showBranchComparison ? this.$t("mira.comparison.emptyBranches").toString() : null;
  }

  /** The comparison card shows its single spinner while the member documents load. */
  get comparisonLoading(): boolean {
    return this.membersLoading;
  }

  /**
   * The comparison the ranking card draws: the downwards one (the parts) where it is on, else the upwards
   * one (the company against the Markt or its Gruppe). With both on, the upwards figure reaches the timeline's
   * legend instead ({@link comparisonLegendValues}).
   */
  get comparisonView(): MiraComparisonView | null {
    return this.downwardComparisonView ?? this.upwardComparisonView;
  }

  /** The company against the Markt or its Gruppe, where a comparison upwards is on. */
  get upwardComparisonView(): MiraComparisonView | null {
    const metricId = this.activeComparisonMetricId;
    if (!metricId) {
      return null;
    }
    if (this.showMarketComparison) {
      return this.withMarketClients(
        buildMarketComparison(
          metricId,
          this.document,
          this.marketProjectsDocument,
          this.marketLabel,
          this.selectedPeriod,
          this.comparisonKey ?? undefined,
          this.trendFormatter(metricId),
          this.viewContext,
          this.branchReferenceLabel,
          this.showPercentiles,
          this.comparisonScaleFor(metricId)
        ),
        metricId
      );
    }
    if (this.showGroupViewComparison) {
      return buildGroupViewComparison(
        metricId,
        this.document,
        this.groupViewDocument,
        this.$t("mira.comparison.referenceGroup").toString(),
        this.selectedPeriod,
        this.comparisonKey ?? undefined,
        this.trendFormatter(metricId),
        this.viewContext,
        this.branchReferenceLabel,
        this.showPercentiles,
        this.comparisonScaleFor(metricId)
      );
    }
    return null;
  }

  /**
   * The parts, where a comparison downwards is on. Branches: the client's own total, one bar per branch the
   * document carries, and the branches with no value. Members: the group total, one bar per member, and the
   * members with no value.
   */
  get downwardComparisonView(): MiraComparisonView | null {
    const metricId = this.activeComparisonMetricId;
    if (!metricId) {
      return null;
    }
    if (this.showInsurerComparison) {
      // The per-insurer document has the per-branch shape: the same builder, named by the insurers.
      return this.withInsurerReason(this.narrowToSavedComparison(buildBranchComparison(
        metricId,
        this.insurerDocument,
        this.document,
        this.insurerNames,
        this.selectedPeriod,
        this.comparisonKey ?? undefined,
        this.trendFormatter(metricId),
        this.viewContext,
        this.branchReferenceLabel,
        this.showPercentiles,
        this.comparisonScaleFor(metricId)
      )));
    }
    if (this.showBranchComparison) {
      return this.narrowToSavedComparison(buildBranchComparison(
        metricId,
        this.branchDocument,
        this.document,
        this.branchNames,
        this.selectedPeriod,
        this.comparisonKey ?? undefined,
        this.trendFormatter(metricId),
        this.viewContext,
        this.branchReferenceLabel,
        this.showPercentiles,
        this.comparisonScaleFor(metricId)
      ));
    }
    if (this.showMemberComparison) {
      return this.narrowToSavedComparison(buildComparison(
        metricId,
        this.document,
        this.memberSectionDocs,
        this.memberNames,
        this.selectedPeriod,
        this.comparisonKey ?? undefined,
        this.trendFormatter(metricId),
        this.viewContext,
        this.$t("mira.comparison.referenceGroup").toString(),
        this.showPercentiles,
        this.comparisonScaleFor(metricId)
      ));
    }
    return null;
  }

  // --- Comparison bars (the Verlauf position): a total compared per member / branch, #2610009 ---

  /**
   * Whether the grouped comparison bars are drawn in the Verlauf position: a comparison is on and the
   * chosen figure is comparable this way. A **total** always is (one sum bar per entity). An **average /
   * share** is too (one mean / share bar per entity, hidden under the case floor; Taimoor, 2026-10-05),
   * but only where at least one entity actually carries a trend for it: an older branch document, or one
   * IRES Cloud has not yet deployed the average trends to, carries none, and the ranking card alone shows.
   * A segment figure never reaches here: it is no period tile. Whether the ranking card follows the bars
   * is {@link showRankingCard}'s call.
   */
  get showComparisonBars(): boolean {
    const metricId = this.activeComparisonMetricId;
    if (!this.showComparison || !metricId || this.comparisonBarsEntities.length === 0) {
      return false;
    }
    // A total needs a timeline of some entity: the per-insurer documents carry none (2026-11-03), and their
    // comparison is the ranking card, never an empty chart.
    if (isTotalMetric(metricId)) {
      return this.comparisonBarsEntities.some((entity) => !entity.reference && !!entity.series);
    }
    // An average is compared at Quartal / Jahr only, so the bars need quarter or year history on both the
    // reference (it scaffolds the periods) and at least one entity; a figure kept by month only (the fleet
    // utilisation) falls back to the ranking card instead of an empty chart.
    const hasCoarse = (series?: MiraTrendSeries) => (series?.quarter?.length ?? 0) > 0 || (series?.year?.length ?? 0) > 0;
    // A compared entity, not the gesamt or Markt bar beside them: those always have a timeline, and with the
    // insurers (no timelines) the chart showed the company's bar alone and hid the ranking card (2026-11-03).
    return hasCoarse(this.trends?.[metricId]) && this.comparisonBarsEntities.some((entity) => !entity.reference && hasCoarse(entity.series));
  }

  /** The reference's own timelines the bars scaffold on: the group's (members) or the client's (branches). */
  get comparisonBarsReferenceTrends(): Record<string, MiraTrendSeries> | undefined {
    return this.trends;
  }

  /**
   * The entities the comparison draws a bar for, the broadest scope on the left (Hans, #2610137; Taimoor:
   * Markt, Gruppe, Mandant, Standort, from bigger to lower): the comparison upwards, the company's own bar,
   * then the parts of the comparison downwards, one per member or per branch the branch document carries a
   * timeline for (plus the "Ohne Standort" / "Weitere Standorte" buckets), worst first and best on the
   * right. Up to eight parts get a bar each; more draw the five levels, in the builder (#2610112). For an
   * average or share the whole comes first as the gesamt bar ({@link comparisonReferenceEntity}); the bar
   * upwards stands before it, both outside the ranking and the levels.
   */
  get comparisonBarsEntities(): MiraStackEntity[] {
    const metricId = this.activeComparisonMetricId;
    if (!metricId) {
      return [];
    }
    if (!this.showDownwardComparison) {
      // Upwards alone: the Markt or the Gruppe beside the company's own bar, over the company's periods
      // (#2610072). Colours follow the entity, not the position.
      if (this.showMarketComparison) {
        return [
          { id: MIRA_MARKET_PROJECTS_ID, label: this.marketLabel, series: this.marketProjectsDocument?.trends?.[metricId] },
          { id: MIRA_MARKET_OWN_ID, label: this.branchReferenceLabel, series: this.trends?.[metricId] },
        ];
      }
      if (this.showGroupViewComparison) {
        return [
          { id: MIRA_GROUP_VIEW_ID, label: this.$t("mira.comparison.referenceGroup").toString(), series: this.groupViewDocument?.trends?.[metricId] },
          { id: MIRA_GROUP_VIEW_OWN_ID, label: this.branchReferenceLabel, series: this.trends?.[metricId] },
        ];
      }
      return [];
    }
    const upward = this.upwardEntity(metricId);
    const reference = this.comparisonReferenceEntity(metricId);
    // A saved comparison keeps its picked order (#2611019: a fixed order, not sorted by the figure).
    const saved = this.activeSavedComparison;
    const parts = this.comparedEntities(metricId);
    const ordered = saved ? inPickedOrder(parts, saved.entityIds) : this.worstFirst(parts);
    return [...(upward ? [upward] : []), ...(reference ? [reference] : []), ...ordered];
  }

  /**
   * The Markt or Gruppe bar before the gesamt bar, where both comparisons are on (Hans, 2026-11-02). Averages
   * and shares only, like the gesamt bar it stands beside (a total is never set against its parts, #2610112
   * X1, and the Markt carries none), and only where the scope above carries a timeline for the figure.
   */
  private upwardEntity(metricId: string): MiraStackEntity | null {
    if (isTotalMetric(metricId)) {
      return null;
    }
    if (this.showMarketComparison) {
      const series = this.marketProjectsDocument?.trends?.[metricId];
      return series ? { id: MIRA_MARKET_PROJECTS_ID, label: this.marketLabel, series, reference: true } : null;
    }
    if (this.showGroupViewComparison) {
      const series = this.groupViewDocument?.trends?.[metricId];
      return series
        ? { id: MIRA_GROUP_VIEW_ID, label: this.$t("mira.comparison.referenceGroup").toString(), series, reference: true }
        : null;
    }
    return null;
  }

  /**
   * The gesamt bar before the Standorte or Mandanten (Hans, #2610137: "Trocknungsdauer gesamt / Standort 1 /
   * Standort 2"): the company's or the group's own figure, as named in the Rangliste. Averages and shares
   * only: a total stays the figure in the card header, never a bar beside its parts (Hans, #2610112 X1).
   */
  private comparisonReferenceEntity(metricId: string): MiraStackEntity | null {
    if (isTotalMetric(metricId)) {
      return null;
    }
    return {
      id: MIRA_COMPARISON_REFERENCE_ID,
      label: this.comparisonView?.reference?.label ?? this.branchReferenceLabel,
      series: this.trends?.[metricId],
      reference: true,
    };
  }

  /** The members or branches of the comparison for one figure, unordered. */
  private comparedEntities(metricId: string): MiraStackEntity[] {
    if (this.showInsurerComparison) {
      const insurerTrends = this.insurerDocument?.trends ?? {};
      const names = this.insurerNames;
      return Object.keys(insurerTrends).map((id) => ({ id, label: names[id] ?? id, series: insurerTrends[id]?.[metricId] }));
    }
    if (this.showBranchComparison) {
      const branchTrends = this.branchDocument?.trends ?? {};
      const names = this.branchNames;
      return Object.keys(branchTrends).map((id) => ({
        id,
        label:
          id === MIRA_BRANCH_NONE
            ? this.$t("mira.comparison.branchNone").toString()
            : id === MIRA_BRANCH_OTHER
            ? this.$t("mira.comparison.branchOther").toString()
            : names[id] ?? this.$t("mira.comparison.branchUnknown").toString(),
        series: branchTrends[id]?.[metricId],
      }));
    }
    return Object.keys(this.memberNames).map((id) => ({
      id,
      label: this.memberNames[id],
      series: this.memberSectionDocs[id]?.trends?.[metricId],
    }));
  }

  /**
   * The entities in the ranking's order turned round: worst first, best on the right (Hans, #2610137: "right
   * and top is better than left and bottom"; the Rangliste keeps the best at the top). An entity without a
   * value in the chosen period stands before the worst, by name.
   */
  private worstFirst(entities: MiraStackEntity[]): MiraStackEntity[] {
    const order = (this.comparisonView?.rows ?? []).map((row) => row.id);
    const rank = (id: string) => (order.includes(id) ? order.indexOf(id) : order.length);
    return [...entities].sort((a, b) => rank(b.id) - rank(a.id) || a.label.localeCompare(b.label));
  }

  /**
   * The Rangliste's entities, best first, from which the timeline picks its five levels (Hans, #2611027): the
   * same five in every period, so the legend, the tooltip and the Rangliste name the same 50 %.
   */
  get comparisonLevelRanking(): string[] {
    return (this.comparisonView?.rows ?? []).filter((row) => !row.reference).map((row) => row.id);
  }

  /**
   * More members or branches than the timeline names (#2610112, above eight): it draws the five levels and
   * the ranking card below names everyone. Never in the Markt, which has two entities.
   */
  get comparisonUsesLevels(): boolean {
    // The gesamt bar and the bar upwards are drawn beside the levels, never counted in them.
    const segments = this.comparisonBarsEntities.filter((entity) => !entity.reference);
    return this.showDownwardComparison && comparisonUsesLevels(segments.length);
  }

  /**
   * Whether the ranking card shows below the timeline (Hans and Taimoor, #2610112 X1 / X4: a card that shows
   * nothing the timeline does not is dropped). Shown for the Markt or a member's "Gruppe" alone (their markers
   * are their own), where the timeline draws the levels and so names no one, and where there is no timeline
   * for the figure at all (the card then carries the figure, or says why a branch comparison has none). With
   * a comparison downwards on, it follows the parts.
   */
  get showRankingCard(): boolean {
    return (
      this.showComparison && (!this.showDownwardComparison || !this.showComparisonBars || this.comparisonUsesLevels)
    );
  }

  /**
   * The reference's figure for the chosen period ("Klünder Trocknungstechnik GmbH: 5.638", "Gruppe gesamt:
   * 7,3 Tage") in the timeline's header, where the ranking card that carried it is not shown (X1: the total
   * as a figure, never as a bar). `null` otherwise or without a value.
   */
  get comparisonHeadline(): string | null {
    const reference = this.comparisonView?.reference ?? null;
    // Where the gesamt bar is drawn, the legend names its figure instead.
    if (!reference || this.showRankingCard || this.comparisonBarsEntities.some((entity) => entity.reference)) {
      return null;
    }
    return this.$t("mira.comparison.headline", [reference.label, reference.valueText]).toString();
  }

  /**
   * Each named entity's figure in the chosen period, for the timeline's legend where the ranking card is not
   * shown: the share then the value for a total ("44 % · 2.497", X1 / X2), the value for an average.
   */
  get comparisonLegendValues(): Record<string, string> | null {
    if (this.showRankingCard) {
      return null;
    }
    const values: Record<string, string> = {};
    (this.comparisonView?.rows ?? []).forEach((row) => {
      values[row.id] = row.shareText ? `${row.shareText} · ${row.valueText}` : row.valueText;
    });
    // The gesamt bar's figure (#2610137): the reference row's.
    const reference = this.comparisonView?.reference;
    if (reference && this.comparisonBarsEntities.some((entity) => entity.id === reference.id)) {
      values[reference.id] = reference.valueText;
    }
    // The bar upwards beside the parts: its row in the comparison upwards.
    const upward = this.comparisonBarsEntities.find((entity) => entity.reference && entity.id !== reference?.id);
    const upwardRow = upward ? this.upwardComparisonView?.rows.find((row) => row.id === upward.id) : undefined;
    if (upward && upwardRow) {
      values[upward.id] = upwardRow.valueText;
    }
    return values;
  }

  /** The row dots of the ranking card: the timeline's colours, none where the timeline draws the levels. */
  get comparisonRankingColors(): Record<string, string> {
    return this.comparisonUsesLevels ? {} : this.comparisonColorMap;
  }

  /**
   * The one stable colour per entity id for the whole comparison, built once from the entities' size (their
   * projects in the chosen period). Every comparison card on every Mira screen reads this same map, so a
   * company / branch keeps its colour across the tiles and the screens while the comparison and period are
   * unchanged. Empty while no comparison is on. Theme-aware (re-reads `$vuetify.theme.dark`).
   *
   * The scope upwards (the Markt, a member's Gruppe) is always a mid grey (Hans, #2610137: the market is the
   * backdrop, not a company), and stays out of the palette, so the company keeps the first colour.
   */
  get comparisonColorMap(): Record<string, string> {
    // Read the reactive values first, then branch (ui-conventions, Reactivity).
    const dark = this.$vuetify.theme.dark;
    const entities = this.comparisonBarsEntities;
    if (!this.showComparison || entities.length === 0) {
      return {};
    }
    const upwardIds = [MIRA_MARKET_PROJECTS_ID, MIRA_GROUP_VIEW_ID];
    const sized: MiraSizedEntity[] = entities
      .filter((entity) => !upwardIds.includes(entity.id) && !entity.reference)
      .map((entity) => ({
        id: entity.id,
        label: entity.label,
        size: this.entitySize(entity.id),
      }));
    const colors = buildStableEntityColors(sized, dark);
    entities
      .filter((entity) => upwardIds.includes(entity.id))
      .forEach((entity) => {
        colors[entity.id] = dark ? COLOR_MIRA_MARKET_BAR_DARK : COLOR_MIRA_MARKET_BAR;
      });
    // The gesamt bar in a darker grey: the reference, not one of the segments (#2610137).
    if (entities.some((entity) => entity.id === MIRA_COMPARISON_REFERENCE_ID)) {
      colors[MIRA_COMPARISON_REFERENCE_ID] = dark ? COLOR_MIRA_REFERENCE_BAR_DARK : COLOR_MIRA_REFERENCE_BAR;
    }
    return colors;
  }

  /** An entity's size in the chosen period (its projects, MIRA-PRJ-001), from the business-development source. */
  private entitySize(entityId: string): number | null {
    const period = this.selectedPeriod;
    if (this.showBranchComparison || this.showInsurerComparison) {
      const sizes = this.showInsurerComparison ? this.insurerBusinessDocument : this.branchBusinessDocument;
      const metric = sizes?.periods[period]?.branches[entityId]?.[MIRA_SIZE_METRIC_ID];
      return metric &&
        metric.state === MiraAvailabilityState.AVAILABLE &&
        metric.value !== null &&
        metric.value !== undefined
        ? metric.value
        : null;
    }
    return publishedMetric(this.memberBusinessDocs[entityId]?.periods[period] ?? null, MIRA_SIZE_METRIC_ID)?.value ?? null;
  }

  /** Turn the comparison upwards on, off or to another scope; a country's entry is the Markt of that country. */
  onUpwardChange(option: string) {
    const country = marketCountryOf(option);
    MiraComparisonModule.setUpward(country ? MIRA_COMPARISON_MARKET : (option as MiraComparisonMode));
    MiraComparisonModule.setMarketCountry(country);
  }

  /**
   * Turn the comparison downwards on or off, or choose a saved comparison (its kind with its id). Loads the
   * member documents the first time members are compared.
   */
  onDownwardChange(option: string) {
    const choice = downwardChoiceOf(option, this.savedComparisons);
    MiraComparisonModule.setDownward(choice);
    if (choice.mode === MIRA_COMPARISON_MEMBERS) {
      this.ensureMembersLoaded();
    }
  }

  /**
   * Load the member clients' names and their own section documents once per visit. The member reads are
   * swallowed per member in the repository, so one unreadable member drops out of the card rather than
   * blanking it.
   */
  async ensureMembersLoaded() {
    if (this.membersLoaded || !this.isGroupMaster) {
      return;
    }
    this.membersLoading = true;
    try {
      // A company's viewing client (#2610112 X6e) compares the company's Mandanten, named and copied into its
      // own documents by IRES Cloud: it may not read them as a master reads its members.
      const companyView = companyViewOf(this.sections);
      const loadMember = companyView ? loadCompanyMemberSection : loadMemberSection;
      // Copied names are mutated below (the master dropped), so the roster's object is not shared.
      const roster = await loadMiraMemberRoster(this.sections);
      const ids = roster.ids;
      const names: Record<string, string> = { ...roster.names };

      const documents = await Promise.all(ids.map((id) => loadMember(id, this.section)));
      const memberDocs: Record<string, MiraSectionDocument | null> = {};
      ids.forEach((id, index) => {
        memberDocs[id] = documents[index];
      });

      // Each member's business-development document sizes it for the stable colour order (its projects in
      // the period). The same cached read as the section above when this screen *is* Geschäftsentwicklung.
      const businessDocs: Record<string, MiraSectionDocument | null> = {};
      if (this.section === MiraSection.BUSINESS_DEVELOPMENT) {
        ids.forEach((id, index) => {
          businessDocs[id] = documents[index];
        });
      } else {
        const sizes = await Promise.all(ids.map((id) => loadMember(id, MiraSection.BUSINESS_DEVELOPMENT)));
        ids.forEach((id, index) => {
          businessDocs[id] = sizes[index];
        });
      }
      // A master can list itself among its members. Without its own figures it is not a company to
      // compare (the group row already stands for it), so it is left out rather than named "Ohne Wert".
      const masterId = getClientId();
      if (masterId && !memberDocs[masterId]) {
        delete names[masterId];
        delete memberDocs[masterId];
        delete businessDocs[masterId];
      }

      this.memberNames = names;
      this.memberSectionDocs = memberDocs;
      this.memberBusinessDocs = businessDocs;
      this.membersLoaded = true;
    } catch (error) {
      // The group list we cannot read leaves the card empty rather than failing the screen.
    } finally {
      this.membersLoading = false;
    }
  }

  /** When the comparison is already on (chosen on another screen), load this screen's member documents. */
  @Watch("isLoading")
  onLoadingSettled(loading: boolean) {
    if (!loading && this.showMemberComparison) {
      this.ensureMembersLoaded();
    }
  }

  /**
   * The Verlauf card's formatter for the figure it shows. A cached getter, never a call in the template:
   * a function built on every render is a new prop each time, so the chart rebuilt its data and replayed
   * its draw-in on every re-render of the screen (seen in the 2026-10-02 QA screenshots).
   */
  get activeTrendFormatter(): (value: number) => string {
    const metricId = this.activeTrendMetricId;
    return this.trendFormatter(metricId ?? "");
  }

  /**
   * The comparison stack's formatter for the selected total. A cached getter, never a call in the
   * template, for the same reason as {@link activeTrendFormatter}: a fresh function each render rebuilds
   * the chart and replays its draw-in.
   */
  get activeComparisonFormatter(): (value: number) => string {
    return this.trendFormatter(this.activeComparisonMetricId ?? "");
  }

  /**
   * Select the figure the Verlauf (or comparison) card shows. In comparison mode any period tile may be
   * chosen; otherwise only one with a trend, so the Verlauf card always has something to draw.
   */
  selectTrendMetric(metricId: string) {
    const selectable = this.comparisonActive
      ? this.periodTiles.some((tile) => tile.id === metricId)
      : this.trendAvailable(metricId);
    if (selectable) {
      this.selectedTrendMetricId = metricId;
    }
  }

  /**
   * The formatter the Verlauf card uses for its points, in the figure's own unit. Each screen provides
   * its own from its view model's `*TrendFormatter`; the base counts, as a safe fallback for a figure a
   * screen does not special-case.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  trendFormatter(metricId: string): (value: number) => string {
    return (value) => formatNumber(value);
  }

  get periods(): string[] {
    return availablePeriods(this.document ?? undefined);
  }

  /**
   * The period this screen shows: the one chosen last on any Mira page, shared through the store
   * (#2610137), or this screen's first where its document lacks it. Choosing one here sets the shared
   * choice, so the landing page and the other screens follow.
   */
  get selectedPeriod(): string {
    return effectivePeriod(MiraPeriodModule.period, this.periods);
  }

  set selectedPeriod(period: string) {
    MiraPeriodModule.setPeriod(period);
  }

  get period(): MiraPublishedPeriod | null {
    // Both read before branching, so the computed tracks both (ui-conventions, Reactivity).
    const selected = this.selectedPeriod;
    const document = this.document;
    return document?.periods[selected] ?? null;
  }

  /** The chosen period's bounds, for the Verlauf card to shade on its fixed history. */
  get selectedRange(): { from: string; toExclusive: string } | null {
    const period = this.period;
    return period ? { from: period.from, toExclusive: period.toExclusive } : null;
  }

  // --- What the changes compare with: the period's own comparison (Hans, #2611021, 2026-11-04) ---

  /** The key of the period the chosen one compares with, where the document publishes it. */
  get comparisonKey(): string | null {
    return baselineKey(this.document, this.selectedPeriod, impliedBaseline(this.selectedPeriod));
  }

  /** The baseline period as published (unscaled), or `null`. */
  private get baselinePeriod(): MiraPublishedPeriod | null {
    const key = this.comparisonKey;
    return key ? this.document?.periods[key] ?? null : null;
  }

  /** The factor a total of the baseline is scaled by: a month against "Letzte 12 Monate" a twelfth. */
  get comparisonScale(): number {
    const period = this.period;
    const baseline = this.baselinePeriod;
    return period && baseline ? baselineScale(period, baseline) : 1;
  }

  /** The scale for one figure: the comparison scale for a total, 1 for an average or a share. */
  comparisonScaleFor(metricId: string): number {
    return countMetricIds(this.document).includes(metricId) ? this.comparisonScale : 1;
  }

  /**
   * The period the chosen one compares with, its totals scaled to the chosen period's length, so every tile's
   * change reads it as it is.
   */
  get comparison(): MiraPublishedPeriod | null {
    const baseline = this.baselinePeriod;
    return baseline ? scaledBaselinePeriod(baseline, this.comparisonScale, countMetricIds(this.document)) : null;
  }

  get periodDescription(): string {
    const period = this.period;
    if (!period) {
      return "";
    }

    const { first, last } = periodRange(period.from, period.toExclusive);
    return this.$t("mira.dataQuality.periodRange", [
      this.periodName(this.selectedPeriod),
      formatDateToDayMonthYear(first),
      formatDateToDayMonthYear(last),
    ]).toString();
  }

  /**
   * "Verglichen mit: Die 12 Monate davor (01.11.2024 bis 31.10.2025)", the comparison named by the period and
   * its dates, and where totals are scaled, a note saying so.
   */
  get comparisonDescription(): string {
    const baseline = this.baselinePeriod;
    if (!baseline) {
      return "";
    }
    const { first, last } = periodRange(baseline.from, baseline.toExclusive);
    const text = this.$t("mira.baseline.comparedWith", [
      this.$t(baselineNameKey(this.selectedPeriod)).toString(),
      formatDateToDayMonthYear(first),
      formatDateToDayMonthYear(last),
    ]).toString();
    return this.comparisonScale === 1 ? text : `${text} · ${this.$t("mira.baseline.scaledNote").toString()}`;
  }

  get generatedAtDescription(): string {
    const generatedAt = this.document?.generatedAt;
    return generatedAt
      ? this.$t("mira.dataQuality.generatedAt", [formatDateToDayMonthYear(new Date(generatedAt))]).toString()
      : "";
  }

  /**
   * An IRES account's "Alle Mandanten" (#2610111): every published Mandant added up. No comparison, and
   * the parts that only make sense for one Mandant (Standorte, Projekttyp-Mix, the unlock list) are hidden.
   */
  get isAllClients(): boolean {
    return this.document?.scope === MIRA_ALL_CLIENTS_SCOPE;
  }

  /** Every section's figures waiting on data in the chosen period. A screen may narrow it. */
  get unlocks(): MiraSectionUnlocks[] {
    return collectUnlocks(this.sections, this.selectedPeriod);
  }

  /** The app bar's title in the user's language, "Mira > Trocknung" (#2611027). */
  @Watch("$i18n.locale")
  setTitle() {
    setMiraPageTitle(this, this.titleKey);
  }

  async mounted() {
    this.setTitle();
    this.isGroupMaster = isMiraGroupMaster();

    try {
      this.sections = await loadMiraSections();

      // "Alle Mandanten" (#2610111) has no comparison, so it reads neither the Markt nor the branches.
      if (this.isAllClients) {
        return;
      }

      // The Markt document (#2610072), so the "Markt" option can appear; a missing one is `null`.
      // Geräte je Projekt is compared with the clients of the same kind (#2610112 X7): the Markt of the
      // client's kind takes its place, so every card below reads it unchanged.
      const own = this.sections[this.section] ?? null;
      this.marketAllDocument = withMarketOfOwnKind(await loadMarketSection("projects", this.section), own);
      // A country chosen on another Mira screen: its Markt too (#2611022).
      await this.onMarketCountry(this.marketCountry);

      // Load the branch document so the "Standorte" option can appear. One extra read per screen, and only
      // off a group master (its comparison is the group) and off Datenqualität (which never has one).
      if (!this.isGroupMaster && this.section !== MiraSection.DATA_QUALITY) {
        this.branchDocument = await loadBranchSection(this.section);
        // The business-development branch document sizes the branches for their stable colour order; the
        // same cached read as above when this screen *is* Geschäftsentwicklung.
        this.branchBusinessDocument =
          this.section === MiraSection.BUSINESS_DEVELOPMENT
            ? this.branchDocument
            : await loadBranchSection(MiraSection.BUSINESS_DEVELOPMENT);
      }

      // The per-insurer document (#2610137, 2026-11-03), a master's pooled one, so "Versicherer" can appear;
      // the business-development one sizes the insurers for their colours. Never on Datenqualität.
      if (this.section !== MiraSection.DATA_QUALITY && !this.isAllClients) {
        this.insurerDocument = await loadInsurerSection(this.section, this.isGroupMaster);
        this.insurerBusinessDocument =
          this.section === MiraSection.BUSINESS_DEVELOPMENT
            ? this.insurerDocument
            : await loadInsurerSection(MiraSection.BUSINESS_DEVELOPMENT, this.isGroupMaster);
      }

      // A member's view of its group (#2610112 X6d), so the "Gruppe" option can appear. Off a master only.
      if (!this.isGroupMaster) {
        this.groupViewDocument = await loadGroupViewSection(this.section);
      }
    } catch (error) {
      SnackbarModule.showError({ key: "mira.dataQuality.loadError" });
    } finally {
      this.isLoading = false;
    }
  }

  periodName(period: string): string {
    return miraPeriodName(period, (key, params) => this.$t(key, params).toString(), this.$i18n?.locale);
  }
}
